import ICatalogItemFacet from "../models/ICatalogItemFacet";
import IPriceRangeFilter from "../models/IPriceRangeFilter";
import { AttributeValue } from "../models/ISearchCatalogItemFilterOptions";

/** Selected facet values keyed by attribute name, e.g. { Color: ["Red", "Blue"], Material: ["Silk"] } */
export type SelectedAttributeFilters = Record<string, string[]>;

/**
 * (De)serializes the ecommerce facet filter selection to/from the `attrs` URL query param and
 * converts it to the socket `options.attributes` contract.
 *
 * URL format: attrName:value1|value2;attrName2:value — names/values URI-encoded so the
 * delimiters (:, |, ;) survive arbitrary attribute text.
 */
export default class FacetFilterService {
    static serializeFacetsToUrl(filters: SelectedAttributeFilters): string {
        const parts: string[] = [];

        for (const [name, values] of Object.entries(filters)) {
            if (!values.length) {
                continue;
            }

            const encodedValues = values.map((value) => encodeURIComponent(value)).join("|");

            parts.push(`${encodeURIComponent(name)}:${encodedValues}`);
        }

        return parts.join(";");
    }

    static parseFacetsFromUrl(serialized: string | null): SelectedAttributeFilters {
        const filters: SelectedAttributeFilters = {};

        if (!serialized) {
            return filters;
        }

        for (const part of serialized.split(";")) {
            const separatorIndex = part.indexOf(":");

            if (separatorIndex <= 0) {
                continue;
            }

            const name = FacetFilterService.safeDecode(part.slice(0, separatorIndex));

            if (name === null) {
                continue;
            }

            const values = part
                .slice(separatorIndex + 1)
                .split("|")
                .filter((value) => value.length)
                .map((value) => FacetFilterService.safeDecode(value))
                .filter((value): value is string => value !== null);

            if (name.length && values.length) {
                filters[name] = values;
            }
        }

        return filters;
    }

    /** The `attrs` param is user-editable; malformed percent-encoding must not throw. */
    private static safeDecode(segment: string): string | null {
        try {
            return decodeURIComponent(segment);
        } catch {
            return null;
        }
    }

    /** Shapes the selection into the `options.attributes` contract sent to the backend. */
    static toAttributeOptions(filters: SelectedAttributeFilters): { [key: string]: AttributeValue } | undefined {
        const attributes: { [key: string]: AttributeValue } = {};
        let hasAny = false;

        for (const [name, values] of Object.entries(filters)) {
            if (values.length) {
                attributes[name] = values;
                hasAny = true;
            }
        }

        return hasAny ? attributes : undefined;
    }

    static toggleValue(filters: SelectedAttributeFilters, name: string, value: string): SelectedAttributeFilters {
        const next: SelectedAttributeFilters = { ...filters };
        const values = next[name] || [];

        if (values.includes(value)) {
            const remaining = values.filter((existingValue) => existingValue !== value);

            if (remaining.length) {
                next[name] = remaining;
            } else {
                delete next[name];
            }
        } else {
            next[name] = [...values, value];
        }

        return next;
    }

    static hasActiveFilters(filters: SelectedAttributeFilters): boolean {
        return Object.values(filters).some((values) => values.length > 0);
    }

    static activeFilterCount(filters: SelectedAttributeFilters): number {
        return Object.values(filters).reduce((total, values) => total + values.length, 0);
    }

    /** Writes minPrice/maxPrice URL params from the price range (removing them when unset). */
    static serializePriceRangeToParams(params: URLSearchParams, priceRange: IPriceRangeFilter | null): void {
        const setBound = (key: "minPrice" | "maxPrice") => {
            const value = priceRange?.[key];

            // Mirror parsePriceRangeFromParams: only finite, non-negative bounds count as set
            if (typeof value === "number" && Number.isFinite(value) && value >= 0) {
                params.set(key, String(value));
            } else {
                params.delete(key);
            }
        };

        setBound("minPrice");
        setBound("maxPrice");
    }

    /** minPrice/maxPrice params are user-editable; NaN and negatives are dropped. */
    static parsePriceRangeFromParams(params: URLSearchParams): IPriceRangeFilter | null {
        const parseBound = (raw: string | null): number | undefined => {
            if (raw === null || !raw.length) {
                return undefined;
            }

            const value = Number(raw);

            return Number.isFinite(value) && value >= 0 ? value : undefined;
        };

        const minPrice = parseBound(params.get("minPrice"));
        const maxPrice = parseBound(params.get("maxPrice"));

        if (minPrice === undefined && maxPrice === undefined) {
            return null;
        }

        return { minPrice, maxPrice };
    }

    static hasActivePriceRange(priceRange: IPriceRangeFilter | null | undefined): boolean {
        return typeof priceRange?.minPrice === "number" || typeof priceRange?.maxPrice === "number";
    }

    static priceRangesEqual(a: IPriceRangeFilter | null, b: IPriceRangeFilter | null): boolean {
        return (a?.minPrice ?? undefined) === (b?.minPrice ?? undefined) && (a?.maxPrice ?? undefined) === (b?.maxPrice ?? undefined);
    }

    /**
     * Drops selections that no longer correspond to a facet (e.g. after a category change the
     * attribute may not exist there). Selected values missing from the facet's value list are
     * kept — self-excluded counts can legitimately omit a still-selected value.
     */
    static pruneToFacets(filters: SelectedAttributeFilters, facets: ICatalogItemFacet[]): SelectedAttributeFilters {
        const facetNames = new Set(facets.map((facet) => facet.name));
        const next: SelectedAttributeFilters = {};

        for (const [name, values] of Object.entries(filters)) {
            if (facetNames.has(name) && values.length) {
                next[name] = values;
            }
        }

        return next;
    }
}
